import { useState, useEffect } from 'react';
import {
  Link2,
  Eye,
  EyeOff,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  Settings,
  ExternalLink,
  TestTube2,
  ChevronDown,
  ChevronUp,
  Clock,
  XCircle,
  BookOpen,
  GraduationCap,
  AlertTriangle,
  Search,
  FileSearch
} from 'lucide-react';
import { authenticatedFetch, getCurrentUser } from '@/lib/auth';
import ReactMarkdown from 'react-markdown';
import { useConfirm } from '../ui';
import StudyPlanVersionPreview from '../StudyPlanVersionPreview';

interface TutorLMSIntegration {
  id: string;
  organization_id: string;
  member_id: string | null;
  organization_name?: string;
  member_name?: string;
  wordpress_url: string;
  client_id: string | null;
  client_secret_encrypted: string | null;
  enabled_by_org: boolean;
  enabled_by_org_at: string | null;
  approved_by_forbund: boolean;
  approved_by_forbund_at: string | null;
  agreement_confirmed: boolean;
  agreement_confirmed_at: string | null;
  quality_control_confirmed: boolean;
  content_alignment_confirmed: boolean;
  assessment_guidelines_confirmed: boolean;
  spot_check_allowed: boolean;
  connection_tested: boolean;
  connection_tested_at: string | null;
  connection_test_error: string | null;
  sync_enabled: boolean;
  sync_interval_minutes: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  mapping_count?: number;
  created_at: string;
}

interface AgreementTemplates {
  [key: string]: {
    name: string;
    content: string;
  };
}

interface TutorCourse {
  id: number;
  title: string;
  slug: string;
  status: string;
  course_duration?: string;
  course_level?: string;
  enrolled_count?: number;
}

type AssessmentType = 'external_sensor' | 'internal_assessor' | 'controlled_exam' | 'unverified';

interface CourseMapping {
  id: string;
  integration_id: string;
  tutor_course_id: number;
  tutor_course_title: string;
  tutor_course_slug: string | null;
  study_plan_id: string | null;
  study_plan_title: string | null;
  study_plan_approval_link: string | null;
  enabled: boolean;
  course_exists: boolean;
  course_missing_checks: number;
  assessment_type: AssessmentType | null;
  assessment_description: string | null;
  controlled_conditions_confirmed: boolean;
  no_self_assessment_confirmed: boolean;
  created_at: string;
}

interface SyncLog {
  id: string;
  integration_id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  courses_checked: number;
  users_checked: number;
  completions_found: number;
  new_completions: number;
  certificates_issued: number;
  errors_count: number;
  triggered_by: string;
}

interface StudyPlanVersion {
  id: string;
  version_number: number;
  approved_at: string;
  title: string;
  ects_credits: number;
}

interface StudyPlan {
  id: string;
  title: string;
  ects_credits: number;
  status?: string;
  approval_link?: string;
  latest_version?: StudyPlanVersion;
}

interface TestResult {
  success: boolean;
  error?: string;
  siteInfo?: {
    sitename: string;
    wordpress_version: string;
    tutor_version: string;
    courses_count: number;
    is_pro: boolean;
  };
  proFeatures?: {
    available: boolean;
    features: string[];
  };
}

export default function TutorLMSSettings() {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<TutorLMSIntegration | null>(null);
  const [mappings, setMappings] = useState<CourseMapping[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [studyPlans, setStudyPlans] = useState<StudyPlan[]>([]);
  const [tutorCourses, setTutorCourses] = useState<TutorCourse[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Form state
  const [wordpressUrl, setWordpressUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [syncInterval, setSyncInterval] = useState(15);

  // Agreement state
  const [templates, setTemplates] = useState<AgreementTemplates>({});
  const [confirmations, setConfirmations] = useState({
    dataAgreement: false,
    qualityControl: false,
    contentAlignment: false,
    assessmentGuidelines: false,
    spotCheckAllowed: false
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('config');
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Search and preview state
  const [studyPlanSearch, setStudyPlanSearch] = useState('');
  const [previewStudyPlanId, setPreviewStudyPlanId] = useState<string | null>(null);

  // New mapping form
  const [newMapping, setNewMapping] = useState({
    tutorCourseId: '',
    tutorCourseTitle: '',
    studyPlanId: '',
    assessmentType: 'internal_assessor' as AssessmentType,
    assessmentDescription: '',
    controlledConditionsConfirmed: false,
    noSelfAssessmentConfirmed: false
  });

  // Check if integration is fully configured and approved
  const isFullyConfigured = integration &&
    integration.client_secret_encrypted &&
    integration.connection_tested &&
    integration.agreement_confirmed &&
    integration.approved_by_forbund;


  useEffect(() => {
    loadData();
  }, []);

  // When integration loads and is fully configured, default to showing mappings
  useEffect(() => {
    if (integration && !initialLoadDone) {
      setInitialLoadDone(true);
      if (isFullyConfigured) {
        setExpandedSection('mappings');
      }
    }
  }, [integration, isFullyConfigured, initialLoadDone]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const user = await getCurrentUser();
      if (!user?.study_association_id) {
        setError('Kunne ikke finne organisasjonstilknytning');
        setLoading(false);
        return;
      }

      setOrganizationId(user.study_association_id);
      setMemberId(user.member_id || null);
      setUserRole(user.role || null);

      await Promise.all([
        loadIntegration(user.member_id, user.role),
        loadStudyPlans(user.id),
        loadAgreementTemplates()
      ]);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadIntegration = async (userMemberId?: string | null, role?: string | null) => {
    try {
      const useMemberId = userMemberId || memberId;
      const useRole = role || userRole;

      let url = '/api/tutorlms/integrations';
      if (useMemberId && useRole === 'member') {
        url = `/api/tutorlms/integrations/member/${useMemberId}`;
      }

      const response = await authenticatedFetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          setIntegration(null);
          return;
        }
        throw new Error('Kunne ikke laste integrasjon');
      }

      const data = await response.json();

      // Handle member endpoint response format
      if (data.integration !== undefined) {
        if (data.integration) {
          const int = data.integration;
          setIntegration(int);
          setWordpressUrl(int.wordpress_url || '');
          setClientId(int.client_id || '');
          setSyncEnabled(int.sync_enabled);
          setSyncInterval(int.sync_interval_minutes || 15);

          if (int.id) {
            await Promise.all([
              loadMappings(int.id),
              loadSyncLogs(int.id)
            ]);
          }
        } else {
          setIntegration(null);
        }
        return;
      }

      // Handle admin endpoint response format (array)
      if (Array.isArray(data) && data.length > 0) {
        const int = data[0];
        setIntegration(int);
        setWordpressUrl(int.wordpress_url || '');
        setClientId(int.client_id || '');
        setSyncEnabled(int.sync_enabled);
        setSyncInterval(int.sync_interval_minutes || 15);

        if (int.id) {
          await Promise.all([
            loadMappings(int.id),
            loadSyncLogs(int.id)
          ]);
        }
      }
    } catch (err: any) {
      console.error('Error loading integration:', err);
    }
  };

  const loadMappings = async (integrationId?: string) => {
    try {
      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/mappings/member/${memberId}`
        : `/api/tutorlms/mappings/${integrationId || integration?.id}`;

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setMappings(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      console.error('Error loading mappings:', err);
    }
  };

  const loadSyncLogs = async (integrationId: string) => {
    try {
      const response = await authenticatedFetch(`/api/tutorlms/sync/logs?integration_id=${integrationId}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setSyncLogs(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      console.error('Error loading sync logs:', err);
    }
  };

  const loadStudyPlans = async (userId?: string) => {
    try {
      let userIdToUse = userId;
      if (!userIdToUse) {
        const user = await getCurrentUser();
        userIdToUse = user?.id;
      }

      if (!userIdToUse) {
        console.error('No user_id available for loading study plans');
        return;
      }

      // Use new versions/approved endpoint to get version info
      const response = await authenticatedFetch('/api/study-plans/versions/approved', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.study_plans) {
          setStudyPlans(data.study_plans);
        }
      }
    } catch (err: any) {
      console.error('Error loading study plans:', err);
    }
  };

  const loadTutorCourses = async () => {
    if (!integration?.id && !memberId) return;

    try {
      setLoadingCourses(true);
      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/courses/member/${memberId}`
        : `/api/tutorlms/courses/${integration?.id}`;

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setTutorCourses(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      console.error('Error loading TutorLMS courses:', err);
      setError('Kunne ikke hente kurs fra TutorLMS');
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadAgreementTemplates = async () => {
    try {
      const response = await authenticatedFetch('/api/tutorlms/agreement-templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || {});
      }
    } catch (err: any) {
      console.error('Error loading agreement templates:', err);
    }
  };

  const handleConfirmAgreements = async () => {
    if (!integration?.id && !memberId) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/integrations/member/${memberId}/confirm-agreements`
        : `/api/tutorlms/integrations/${integration?.id}/confirm-agreements`;

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(confirmations)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke bekrefte avtaler');
      }

      setSuccess('Avtaler bekreftet. Integrasjonen venter nå på godkjenning fra studieforbundet.');
      await loadIntegration();

    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const allAgreementsConfirmed = Object.values(confirmations).every(v => v);

  const handleSaveIntegration = async () => {
    if (!organizationId) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!wordpressUrl) {
        setError('WordPress URL er påkrevd');
        return;
      }

      const isMemberUser = memberId && userRole === 'member';

      if (!integration) {
        // Create new integration
        let url = '/api/tutorlms/integrations';
        let body: any = {
          organization_id: organizationId,
          wordpress_url: wordpressUrl,
          client_id: clientId || undefined,
          client_secret: clientSecret || undefined
        };

        if (isMemberUser) {
          url = `/api/tutorlms/integrations/member/${memberId}`;
          body = {
            wordpress_url: wordpressUrl,
            client_id: clientId || undefined,
            client_secret: clientSecret || undefined,
            sync_interval_minutes: syncInterval
          };
        }

        const response = await authenticatedFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          setSuccess('Integrasjon opprettet');
          await loadIntegration();
          setClientSecret('');
        } else {
          const data = await response.json();
          setError(data.error || 'Kunne ikke opprette integrasjon');
        }
      } else {
        // Update existing integration
        const updates: any = {
          wordpress_url: wordpressUrl,
          client_id: clientId,
          sync_enabled: syncEnabled,
          sync_interval_minutes: syncInterval
        };

        if (clientSecret) {
          updates.client_secret = clientSecret;
        }

        let url = `/api/tutorlms/integrations/${integration.id}`;
        let method = 'PATCH';

        if (isMemberUser) {
          url = `/api/tutorlms/integrations/member/${memberId}`;
          method = 'POST';
        }

        const response = await authenticatedFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });

        if (response.ok) {
          setSuccess('Innstillinger lagret');
          await loadIntegration();
          setClientSecret('');
        } else {
          const data = await response.json();
          setError(data.error || 'Kunne ikke lagre innstillinger');
        }
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!integration?.id && !memberId) return;

    try {
      setTesting(true);
      setTestResult(null);

      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/integrations/member/${memberId}/test`
        : `/api/tutorlms/integrations/${integration?.id}/test`;

      const response = await authenticatedFetch(url, {
        method: 'POST'
      });

      const data = await response.json();
      setTestResult(data);

      if (data.success) {
        await loadIntegration();
        await loadTutorCourses();
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        error: err.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleManualSync = async () => {
    if (!integration?.id && !memberId) return;

    try {
      setSyncing(true);
      setError('');
      setSuccess('');

      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/sync/member/${memberId}`
        : '/api/tutorlms/sync';

      const body = isMemberUser
        ? {}
        : { integration_id: integration?.id };

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Synkronisering startet');
        setTimeout(async () => {
          await loadIntegration();
          if (integration?.id) {
            await loadSyncLogs(integration.id);
          }
        }, 5000);
      } else {
        setError(data.error || 'Synkronisering feilet');
      }

      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleAddMapping = async () => {
    if (!newMapping.tutorCourseId) {
      setError('Velg et TutorLMS-kurs');
      return;
    }

    if (!newMapping.studyPlanId) {
      setError('Velg en studieplan');
      return;
    }

    if (!newMapping.noSelfAssessmentConfirmed) {
      setError('Du må bekrefte at kandidatene ikke foretar egenvurdering');
      return;
    }

    if (newMapping.assessmentType === 'controlled_exam' && !newMapping.controlledConditionsConfirmed) {
      setError('Du må bekrefte at eksamen gjennomføres under kontrollerte forhold');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const selectedCourse = tutorCourses.find(c => c.id === parseInt(newMapping.tutorCourseId));

      const isMemberUser = memberId && userRole === 'member';
      const url = isMemberUser
        ? `/api/tutorlms/mappings/member/${memberId}`
        : '/api/tutorlms/mappings';

      const body: any = {
        tutor_course_id: parseInt(newMapping.tutorCourseId),
        tutor_course_title: selectedCourse?.title || newMapping.tutorCourseTitle,
        tutor_course_slug: selectedCourse?.slug,
        study_plan_id: newMapping.studyPlanId,
        assessment_type: newMapping.assessmentType,
        assessment_description: newMapping.assessmentDescription || null,
        controlled_conditions_confirmed: newMapping.controlledConditionsConfirmed,
        no_self_assessment_confirmed: newMapping.noSelfAssessmentConfirmed
      };

      if (!isMemberUser && integration?.id) {
        body.integration_id = integration.id;
      }

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setSuccess('Kobling opprettet');
        setShowAddMapping(false);
        setNewMapping({
          tutorCourseId: '',
          tutorCourseTitle: '',
          studyPlanId: '',
          assessmentType: 'internal_assessor',
          assessmentDescription: '',
          controlledConditionsConfirmed: false,
          noSelfAssessmentConfirmed: false
        });
        await loadMappings();
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke opprette kobling');
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    const confirmed = await confirm({
      title: 'Slett kobling',
      message: 'Er du sikker på at du vil slette denne koblingen?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/tutorlms/mappings/${mappingId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Kobling slettet');
        if (integration?.id) {
          await loadMappings(integration.id);
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke slette kobling');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleMapping = async (mapping: CourseMapping) => {
    try {
      const response = await authenticatedFetch(`/api/tutorlms/mappings/${mapping.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !mapping.enabled })
      });

      if (response.ok) {
        if (integration?.id) {
          await loadMappings(integration.id);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <GraduationCap className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">TutorLMS-integrasjon</h2>
            <p className="text-sm text-gray-500">
              Koble TutorLMS-kurs til studieplaner for automatisk attestutstedelse
            </p>
          </div>
        </div>

        {/* Status badges */}
        {integration && (
          <div className="flex flex-wrap gap-2 mt-4">
            <span className={`px-2 py-1 text-xs rounded-full ${
              integration.client_secret_encrypted
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {integration.client_secret_encrypted ? 'API-tilgang konfigurert' : 'Mangler API-tilgang'}
            </span>
            <span className={`px-2 py-1 text-xs rounded-full ${
              integration.approved_by_forbund
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {integration.approved_by_forbund ? 'Godkjent av forbund' : 'Venter på godkjenning'}
            </span>
            <span className={`px-2 py-1 text-xs rounded-full ${
              integration.sync_enabled
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {integration.sync_enabled ? 'Synkronisering aktiv' : 'Synkronisering av'}
            </span>
          </div>
        )}
      </div>

      {/* TutorLMS Pro Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Krever TutorLMS Pro</h3>
            <p className="text-sm text-amber-700 mt-1">
              Automatisk synkronisering av kursgjennomføringer krever <strong>TutorLMS Pro</strong>.
              Gratisversjonen av TutorLMS har ikke API-støtte for å hente fullføringsdata.
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {/* Configuration Section - Show at top when NOT fully configured */}
      {!isFullyConfigured && (
      <div className="bg-white rounded-lg shadow-sm border">
        <button
          onClick={() => toggleSection('config')}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <Settings className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-900">Konfigurasjon</span>
          </div>
          {expandedSection === 'config' ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </button>

        {expandedSection === 'config' && (
          <div className="px-6 pb-6 space-y-4 border-t">
            <div className="pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WordPress URL *
              </label>
              <input
                type="url"
                value={wordpressUrl}
                onChange={(e) => setWordpressUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Basis-URL til din WordPress-side med TutorLMS
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application Password Brukernavn (Client ID) *
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="admin"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                WordPress-brukernavnet som har Application Password
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application Password {integration?.client_secret_encrypted ? '(allerede satt)' : '*'}
              </label>
              <div className="relative">
                <input
                  type={showClientSecret ? 'text' : 'password'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={integration?.client_secret_encrypted ? '••••••••' : 'xxxx xxxx xxxx xxxx xxxx xxxx'}
                  className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  type="button"
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showClientSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Opprett i WordPress: Brukere → Din profil → Application Passwords
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Synkroniseringsintervall
                </label>
                <select
                  value={syncInterval}
                  onChange={(e) => setSyncInterval(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={5}>Hvert 5. minutt</option>
                  <option value={15}>Hvert 15. minutt</option>
                  <option value={30}>Hvert 30. minutt</option>
                  <option value={60}>Hver time</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={syncEnabled}
                    onChange={(e) => setSyncEnabled(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Aktiver synkronisering</span>
                </label>
              </div>
            </div>

            {!integration && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="w-4 h-4 mt-1 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">
                    Jeg godtar vilkårene for LMS-integrasjon og bekrefter at vi har rett til
                    å hente kursdata fra TutorLMS for attestutstedelse.
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={handleSaveIntegration}
                disabled={saving || (!integration && !agreementAccepted)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {integration ? 'Lagre endringer' : 'Opprett integrasjon'}
              </button>

              {integration && (
                <>
                  <button
                    onClick={handleTestConnection}
                    disabled={testing || !integration.client_secret_encrypted}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}
                    Test tilkobling
                  </button>

                  <button
                    onClick={handleManualSync}
                    disabled={syncing || !integration.client_secret_encrypted || !integration.approved_by_forbund}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Synkroniser nå
                  </button>
                </>
              )}
            </div>

            {/* Test result */}
            {testResult && (
              <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className={`font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? 'Tilkobling vellykket!' : 'Tilkobling feilet'}
                  </span>
                </div>

                {testResult.success && testResult.siteInfo && (
                  <div className="mt-2 text-sm text-gray-600 space-y-1">
                    <p><strong>Nettsted:</strong> {testResult.siteInfo.sitename}</p>
                    <p><strong>WordPress-versjon:</strong> {testResult.siteInfo.wordpress_version}</p>
                    <p><strong>TutorLMS-versjon:</strong> {testResult.siteInfo.tutor_version}</p>
                    <p><strong>Antall kurs:</strong> {testResult.siteInfo.courses_count}</p>
                    <p className={testResult.siteInfo.is_pro ? 'text-green-600' : 'text-amber-600'}>
                      <strong>TutorLMS Pro:</strong> {testResult.siteInfo.is_pro ? 'Ja' : 'Nei (begrenset funksjonalitet)'}
                    </p>
                  </div>
                )}

                {testResult.proFeatures && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Pro-funksjoner:</p>
                    <div className="space-y-1">
                      {testResult.proFeatures.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {testResult.proFeatures?.available ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <span className={testResult.proFeatures?.available ? 'text-gray-600' : 'text-red-600'}>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {testResult.error && (
                  <p className="mt-2 text-sm text-red-600">{testResult.error}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Agreements Section */}
      {integration && integration.client_secret_encrypted && integration.connection_tested && !integration.agreement_confirmed && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-purple-100 text-purple-700">
                2
              </div>
              <h3 className="font-medium text-gray-900">Bekreft avtaler</h3>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              For å aktivere integrasjonen må du lese og bekrefte følgende avtaler:
            </p>

            {/* Data Processing Agreement */}
            {templates.tutorlms_data_agreement && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">{templates.tutorlms_data_agreement.name}</h4>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                  <ReactMarkdown>{templates.tutorlms_data_agreement.content}</ReactMarkdown>
                </div>
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmations.dataAgreement}
                      onChange={(e) => setConfirmations({ ...confirmations, dataAgreement: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Jeg bekrefter at jeg har lest og forstått denne avtalen</span>
                  </label>
                </div>
              </div>
            )}

            {/* Quality Control */}
            {templates.tutorlms_quality_control && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">{templates.tutorlms_quality_control.name}</h4>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                  <ReactMarkdown>{templates.tutorlms_quality_control.content}</ReactMarkdown>
                </div>
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmations.qualityControl}
                      onChange={(e) => setConfirmations({ ...confirmations, qualityControl: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Jeg bekrefter kvalitetskontrollkravene</span>
                  </label>
                </div>
              </div>
            )}

            {/* Content Alignment */}
            {templates.tutorlms_content_alignment && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">{templates.tutorlms_content_alignment.name}</h4>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                  <ReactMarkdown>{templates.tutorlms_content_alignment.content}</ReactMarkdown>
                </div>
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmations.contentAlignment}
                      onChange={(e) => setConfirmations({ ...confirmations, contentAlignment: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Jeg bekrefter innholdssamsvar</span>
                  </label>
                </div>
              </div>
            )}

            {/* Assessment Guidelines */}
            {templates.tutorlms_assessment_guidelines && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">{templates.tutorlms_assessment_guidelines.name}</h4>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                  <ReactMarkdown>{templates.tutorlms_assessment_guidelines.content}</ReactMarkdown>
                </div>
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmations.assessmentGuidelines}
                      onChange={(e) => setConfirmations({ ...confirmations, assessmentGuidelines: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Jeg bekrefter vurderingsretningslinjene</span>
                  </label>
                </div>
              </div>
            )}

            {/* Spot Check */}
            {templates.tutorlms_spot_check && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">{templates.tutorlms_spot_check.name}</h4>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                  <ReactMarkdown>{templates.tutorlms_spot_check.content}</ReactMarkdown>
                </div>
                <div className="bg-gray-50 px-4 py-3 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmations.spotCheckAllowed}
                      onChange={(e) => setConfirmations({ ...confirmations, spotCheckAllowed: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Jeg aksepterer stikkprøvekontroll</span>
                  </label>
                </div>
              </div>
            )}

            {/* Confirm button */}
            <div className="pt-4">
              <button
                onClick={handleConfirmAgreements}
                disabled={saving || !allAgreementsConfirmed}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Lagrer...' : 'Bekreft alle avtaler og aktiver integrasjon'}
              </button>
              {!allAgreementsConfirmed && (
                <p className="text-sm text-gray-500 text-center mt-2">
                  Du må bekrefte alle avtaler ovenfor
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status when agreements confirmed but waiting for approval */}
      {integration && integration.agreement_confirmed && !integration.approved_by_forbund && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-yellow-600" />
            <div>
              <h3 className="font-medium text-yellow-800">Venter på godkjenning</h3>
              <p className="text-sm text-yellow-700">
                Integrasjonen venter på godkjenning fra studieforbundet. Du vil motta beskjed når integrasjonen er godkjent.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status when fully active */}
      {integration && integration.approved_by_forbund && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-medium text-green-800">Integrasjon aktiv</h3>
              <p className="text-sm text-green-700">
                TutorLMS-integrasjonen er godkjent og aktiv. Fullføringer vil synkroniseres automatisk.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main sections container */}
      <div className="flex flex-col gap-6">
        {/* Sync History Section */}
        {integration && (
          <div className={`bg-white rounded-lg shadow-sm border ${isFullyConfigured ? 'order-1' : 'order-3'}`}>
            <button
              onClick={() => toggleSection('history')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">Synkroniseringshistorikk</span>
              </div>
              {expandedSection === 'history' ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSection === 'history' && (
              <div className="px-6 pb-6 border-t">
                {/* Statistics */}
                <div className="pt-4 grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{integration.total_synced_completions}</p>
                    <p className="text-xs text-blue-600">Gjennomføringer synkronisert</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{integration.total_certificates_issued}</p>
                    <p className="text-xs text-green-600">Attester utstedt</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-600">
                      {integration.last_sync_at ? formatDate(integration.last_sync_at) : 'Aldri'}
                    </p>
                    <p className="text-xs text-gray-500">Siste synkronisering</p>
                  </div>
                </div>

                {/* Sync logs */}
                {syncLogs.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    Ingen synkroniseringslogg ennå
                  </p>
                ) : (
                  <div className="space-y-2">
                    {syncLogs.map(log => (
                      <div key={log.id} className="p-3 border rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {log.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                            {log.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                            {log.status === 'running' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                            {log.status === 'partial' && <AlertCircle className="h-4 w-4 text-yellow-500" />}
                            <span className="text-gray-600">{formatDate(log.started_at)}</span>
                          </div>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">
                            {log.triggered_by === 'manual' ? 'Manuell' : 'Automatisk'}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-4 gap-2 text-xs text-gray-500">
                          <span>Kurs: {log.courses_checked}</span>
                          <span>Brukere: {log.users_checked}</span>
                          <span>Gjennomføringer: {log.completions_found}</span>
                          <span className="text-green-600">Attester: {log.certificates_issued}</span>
                        </div>
                        {log.errors_count > 0 && (
                          <p className="mt-1 text-xs text-red-500">
                            {log.errors_count} feil under synkronisering
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Course Mappings Section */}
        {integration && (
          <div className="bg-white rounded-lg shadow-sm border order-2">
            <button
              onClick={() => toggleSection('mappings')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Link2 className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">
                  Kurskoblinger ({mappings.length})
                </span>
              </div>
              {expandedSection === 'mappings' ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSection === 'mappings' && (
              <div className="px-6 pb-6 border-t">
                <div className="pt-4 mb-4 flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Koble TutorLMS-kurs til studieplaner for automatisk attestutstedelse
                  </p>
                  <button
                    onClick={() => {
                      setShowAddMapping(true);
                      if (tutorCourses.length === 0) {
                        loadTutorCourses();
                      }
                    }}
                    disabled={!integration.client_secret_encrypted}
                    className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Ny kobling
                  </button>
                </div>

                {/* Add mapping form */}
                {showAddMapping && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium text-gray-900 mb-3">Ny kurskobling</h4>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          TutorLMS-kurs *
                        </label>
                        {loadingCourses ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Henter kurs...
                          </div>
                        ) : (
                          <select
                            value={newMapping.tutorCourseId}
                            onChange={(e) => setNewMapping({ ...newMapping, tutorCourseId: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Velg kurs...</option>
                            {tutorCourses.map(course => (
                              <option key={course.id} value={course.id}>
                                {course.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Studieplan *
                        </label>
                        {/* Search input */}
                        <div className="relative mb-2">
                          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Søk etter studieplan..."
                            value={studyPlanSearch}
                            onChange={(e) => setStudyPlanSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                        </div>
                        {/* Dropdown with preview button */}
                        <div className="flex space-x-2">
                          <select
                            value={newMapping.studyPlanId}
                            onChange={(e) => setNewMapping({ ...newMapping, studyPlanId: e.target.value })}
                            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Velg studieplan...</option>
                            {studyPlans
                              .filter(sp =>
                                studyPlanSearch === '' ||
                                sp.title.toLowerCase().includes(studyPlanSearch.toLowerCase())
                              )
                              .map(sp => {
                                const version = sp.latest_version;
                                const versionInfo = version
                                  ? `v${version.version_number}, godkjent ${new Date(version.approved_at).toLocaleDateString('nb-NO')}, `
                                  : '';
                                return (
                                  <option key={sp.id} value={sp.id}>
                                    {sp.title} ({versionInfo}{sp.ects_credits} ECTS)
                                  </option>
                                );
                              })}
                          </select>
                          <button
                            type="button"
                            onClick={() => newMapping.studyPlanId && setPreviewStudyPlanId(newMapping.studyPlanId)}
                            disabled={!newMapping.studyPlanId}
                            className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Forhåndsvis studieplan"
                          >
                            <FileSearch size={18} className="text-gray-600" />
                          </button>
                        </div>
                        {studyPlanSearch && studyPlans.filter(sp =>
                          sp.title.toLowerCase().includes(studyPlanSearch.toLowerCase())
                        ).length === 0 && (
                          <p className="text-sm text-gray-500 mt-1">
                            Ingen studieplaner matcher "{studyPlanSearch}"
                          </p>
                        )}
                      </div>

                      {/* Assessment type selection */}
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-medium text-blue-900 mb-2">Kompetansevurdering</h5>
                        <p className="text-sm text-blue-700 mb-3">
                          Velg hvordan kandidatenes kompetanse vurderes i dette kurset.
                          Dette vises på kompetanseattesten.
                        </p>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vurderingstype *
                          </label>
                          <select
                            value={newMapping.assessmentType}
                            onChange={(e) => setNewMapping({
                              ...newMapping,
                              assessmentType: e.target.value as AssessmentType,
                              controlledConditionsConfirmed: false
                            })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="external_sensor">Ekstern sensor</option>
                            <option value="internal_assessor">Intern vurdering (kursleverandør)</option>
                            <option value="controlled_exam">Eksamen under kontrollerte forhold</option>
                          </select>
                        </div>

                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Beskrivelse av vurdering (valgfritt)
                          </label>
                          <textarea
                            value={newMapping.assessmentDescription}
                            onChange={(e) => setNewMapping({ ...newMapping, assessmentDescription: e.target.value })}
                            placeholder="F.eks. 'Praktisk prøve med ekstern sensor' eller 'Flervalgstest med bestått-grense 80%'"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                            rows={2}
                          />
                        </div>

                        {/* Confirmations */}
                        <div className="mt-4 space-y-3">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newMapping.noSelfAssessmentConfirmed}
                              onChange={(e) => setNewMapping({ ...newMapping, noSelfAssessmentConfirmed: e.target.checked })}
                              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              <strong>Jeg bekrefter</strong> at kandidatene <strong>ikke</strong> foretar egenvurdering av sin kompetanse.
                              Kompetansen vurderes av kursleverandør, sensor, eller gjennom en kontrollert prøve. *
                            </span>
                          </label>

                          {newMapping.assessmentType === 'controlled_exam' && (
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newMapping.controlledConditionsConfirmed}
                                onChange={(e) => setNewMapping({ ...newMapping, controlledConditionsConfirmed: e.target.checked })}
                                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                <strong>Jeg bekrefter</strong> at eksamen gjennomføres under kontrollerte forhold
                                (f.eks. overvåket prøve, tidsbegrensning, identitetsverifisering). *
                              </span>
                            </label>
                          )}
                        </div>
                      </div>

                    </div>

                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={handleAddMapping}
                        disabled={saving || !newMapping.noSelfAssessmentConfirmed || (newMapping.assessmentType === 'controlled_exam' && !newMapping.controlledConditionsConfirmed)}
                        className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Lagre
                      </button>
                      <button
                        onClick={() => setShowAddMapping(false)}
                        className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                )}

                {/* Mappings list */}
                {mappings.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    Ingen kurskoblinger ennå
                  </p>
                ) : (
                  <div className="space-y-2">
                    {mappings.map(mapping => (
                      <div
                        key={mapping.id}
                        className={`p-3 border rounded-lg ${
                          !mapping.enabled ? 'bg-gray-50 opacity-60' :
                          !mapping.course_exists ? 'bg-red-50 border-red-200' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <BookOpen className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {mapping.tutor_course_title}
                                {mapping.tutor_course_slug && (
                                  <span className="text-gray-500 font-normal ml-2">
                                    ({mapping.tutor_course_slug})
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">
                                {mapping.study_plan_title ? (
                                  <>{'\u2192'} {mapping.study_plan_title}</>
                                ) : (
                                  <span className="text-yellow-600">Ingen studieplan valgt</span>
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {!mapping.course_exists && (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                                Kurs ikke funnet
                              </span>
                            )}
                            <button
                              onClick={() => handleToggleMapping(mapping)}
                              className={`px-2 py-1 text-xs rounded ${
                                mapping.enabled
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {mapping.enabled ? 'Aktiv' : 'Inaktiv'}
                            </button>
                            <button
                              onClick={() => handleDeleteMapping(mapping.id)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Configuration Section - Show at bottom when fully configured */}
        {isFullyConfigured && (
          <div className="bg-white rounded-lg shadow-sm border order-3">
            <button
              onClick={() => toggleSection('config')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">Konfigurasjon</span>
              </div>
              {expandedSection === 'config' ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSection === 'config' && (
              <div className="px-6 pb-6 space-y-4 border-t">
                <div className="pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WordPress URL *
                  </label>
                  <input
                    type="url"
                    value={wordpressUrl}
                    onChange={(e) => setWordpressUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Basis-URL til din WordPress-side med TutorLMS
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application Password Brukernavn (Client ID) *
                  </label>
                  <input
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="admin"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application Password {integration?.client_secret_encrypted ? '(allerede satt)' : '*'}
                  </label>
                  <div className="relative">
                    <input
                      type={showClientSecret ? 'text' : 'password'}
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder={integration?.client_secret_encrypted ? '••••••••' : 'xxxx xxxx xxxx xxxx xxxx xxxx'}
                      className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showClientSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Synkroniseringsintervall
                    </label>
                    <select
                      value={syncInterval}
                      onChange={(e) => setSyncInterval(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value={5}>Hvert 5. minutt</option>
                      <option value={15}>Hvert 15. minutt</option>
                      <option value={30}>Hvert 30. minutt</option>
                      <option value={60}>Hver time</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={syncEnabled}
                        onChange={(e) => setSyncEnabled(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Aktiver synkronisering</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  <button
                    onClick={handleSaveIntegration}
                    disabled={saving}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Lagre endringer
                  </button>

                  <button
                    onClick={handleTestConnection}
                    disabled={testing || !integration?.client_secret_encrypted}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}
                    Test tilkobling
                  </button>

                  <button
                    onClick={handleManualSync}
                    disabled={syncing || !integration?.client_secret_encrypted || !integration?.approved_by_forbund}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Synkroniser nå
                  </button>
                </div>

                {/* Test result */}
                {testResult && (
                  <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {testResult.success ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {testResult.success ? 'Tilkobling vellykket!' : 'Tilkobling feilet'}
                      </span>
                    </div>

                    {testResult.success && testResult.siteInfo && (
                      <div className="mt-2 text-sm text-gray-600 space-y-1">
                        <p><strong>Nettsted:</strong> {testResult.siteInfo.sitename}</p>
                        <p><strong>WordPress-versjon:</strong> {testResult.siteInfo.wordpress_version}</p>
                        <p><strong>TutorLMS-versjon:</strong> {testResult.siteInfo.tutor_version}</p>
                        <p><strong>Antall kurs:</strong> {testResult.siteInfo.courses_count}</p>
                        <p className={testResult.siteInfo.is_pro ? 'text-green-600' : 'text-amber-600'}>
                          <strong>TutorLMS Pro:</strong> {testResult.siteInfo.is_pro ? 'Ja' : 'Nei (begrenset funksjonalitet)'}
                        </p>
                      </div>
                    )}

                    {testResult.error && (
                      <p className="mt-2 text-sm text-red-600">{testResult.error}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-medium text-purple-800 mb-2">Slik setter du opp TutorLMS API-tilgang</h3>
        <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
          <li>Logg inn som administrator i WordPress</li>
          <li>Gå til <strong>Brukere</strong> og velg din brukerprofil</li>
          <li>Scroll ned til <strong>Application Passwords</strong></li>
          <li>Skriv inn et navn (f.eks. "Kompetanseattest") og klikk <strong>Add New Application Password</strong></li>
          <li>Kopier passordet som vises (dette vises bare én gang!)</li>
          <li>Bruk ditt WordPress-brukernavn som Client ID og det genererte passordet som Application Password ovenfor</li>
        </ol>
        <div className="mt-3 p-3 bg-amber-100 border border-amber-200 rounded text-sm text-amber-800">
          <strong>Viktig:</strong> For å hente kursgjennomføringer må du ha <strong>TutorLMS Pro</strong> installert.
          Gratisversjonen støtter ikke API-tilgang til fullføringsdata.
        </div>
        <p className="mt-3 text-sm text-purple-600">
          <ExternalLink className="h-4 w-4 inline mr-1" />
          <a
            href="https://developer.wordpress.org/rest-api/using-the-rest-api/authentication/#application-passwords"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Les mer om WordPress Application Passwords
          </a>
        </p>
      </div>

      {/* Study Plan Preview Modal */}
      {previewStudyPlanId && (
        <StudyPlanVersionPreview
          studyPlanId={previewStudyPlanId}
          isOpen={true}
          onClose={() => setPreviewStudyPlanId(null)}
        />
      )}
    </div>
  );
}
